import { AlgorithmResult, OptimizationResults } from '../types';

/**
 * Normalizes values to [0, 1] range using min-max normalization
 * @param value - Value to normalize
 * @param min - Minimum value in the range
 * @param max - Maximum value in the range
 * @returns Normalized value in [0, 1]
 */
export function minMaxNormalize(value: number, min: number, max: number): number {
  if (max === min) return 0.5; // Avoid division by zero
  return (value - min) / (max - min);
}

/**
 * Finds min and max values across all algorithms and all runs for a specific metric
 * @param allRuns - Array of results from all runs (each run contains results for all algorithms)
 * @param metricExtractor - Function to extract the metric value from AlgorithmResult
 * @returns Object with min and max values
 */
export function findGlobalMinMax(
  allRuns: OptimizationResults[],
  metricExtractor: (result: AlgorithmResult) => number
): { min: number; max: number } {
  let min = Infinity;
  let max = -Infinity;

  const algorithmKeys = ['ga', 'culture', 'frog', 'sflaga', 'sflaca', 'pso', 'gapso'] as const;

  for (const run of allRuns) {
    for (const key of algorithmKeys) {
      const result = run[key];
      if (result) {
        const value = metricExtractor(result);
        if (!isNaN(value) && isFinite(value)) {
          min = Math.min(min, value);
          max = Math.max(max, value);
        }
      }
    }
  }

  // Fallback if no valid values found
  if (min === Infinity || max === -Infinity) {
    return { min: 0, max: 1 };
  }

  return { min, max };
}

/**
 * Normalizes fitness, energy, and cost across all algorithms and all runs
 * Energy and cost are inverted (since they are minimization objectives)
 * @param allRuns - Array of results from all runs
 * @returns Normalization ranges and normalized values per run per algorithm
 */
export interface NormalizedResults {
  ranges: {
    fitness: { min: number; max: number };
    energy: { min: number; max: number };
    cost: { min: number; max: number };
  };
  normalized: Array<{
    algorithmKey: string;
    runId: number;
    fitnessNorm: number;
    energyNorm: number;  // Already inverted (1 - normalized)
    costNorm: number;    // Already inverted (1 - normalized)
  }>;
}

export function normalizeAcrossAlgorithms(
  allRuns: OptimizationResults[]
): NormalizedResults {
  const algorithmKeys = ['ga', 'culture', 'frog', 'sflaga', 'sflaca', 'pso', 'gapso'] as const;

  // Find global min/max for each metric
  const fitnessRange = findGlobalMinMax(allRuns, (r) => r.fitness);
  const energyRange = findGlobalMinMax(allRuns, (r) => r.energy);
  const costRange = findGlobalMinMax(allRuns, (r) => r.cost);

  // Normalize all values
  const normalized: NormalizedResults['normalized'] = [];

  allRuns.forEach((run, runId) => {
    algorithmKeys.forEach((algorithmKey) => {
      const result = run[algorithmKey];
      if (result) {
        // Normalize fitness (maximize - higher is better, so no inversion)
        const fitnessNorm = minMaxNormalize(result.fitness, fitnessRange.min, fitnessRange.max);

        // Normalize energy (minimize - lower is better, so invert)
        const energyNormRaw = minMaxNormalize(result.energy, energyRange.min, energyRange.max);
        const energyNorm = 1 - energyNormRaw; // Invert: lower energy = higher normalized value

        // Normalize cost (minimize - lower is better, so invert)
        const costNormRaw = minMaxNormalize(result.cost, costRange.min, costRange.max);
        const costNorm = 1 - costNormRaw; // Invert: lower cost = higher normalized value

        normalized.push({
          algorithmKey,
          runId,
          fitnessNorm,
          energyNorm,
          costNorm,
        });
      }
    });
  });

  return {
    ranges: {
      fitness: fitnessRange,
      energy: energyRange,
      cost: costRange,
    },
    normalized,
  };
}

